import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Loader } from '../../../../../../../../common/react/components/Loader';
import { getTranslation } from '../../../../../../../../common/react/utils/translations';
import {
  performanceChartDataTypeSelector,
  performanceChartNavSelector,
  performanceChartDataSelector} from '../../../../../../store/js/modules/statspro/performance-chart/selectors';
import { FETCH_PERFORMANCE_CHART_STATS } from '../../../../../../store/js/modules/statspro/performance-chart/actions';
import { STATS_CATEGORY_IDS } from '../../../common/config/performance-stats';
import { EmptyState } from '../../../../../../../../common/react/components/EmptyState';

import { PerformanceChartBarChart, PerformanceChartLineChart } from './components'

export const PerformanceChartContainer = () => {
  const dispatch = useDispatch();
  const playerData = useSelector( performanceChartDataSelector );
  const nav = useSelector( performanceChartNavSelector );
  const dataType = useSelector( performanceChartDataTypeSelector );
  const playerId = new URLSearchParams( location.search ).get( 'playerId' );
  const seasons = useSelector( state => state.seasons[ `competition_${ nav.competition.id }` ]?.list ?? [] );
  const season = seasons.find( item => item.id === nav.season.id );
  
  const seasonId = season?.providerId ?? '';
  const playerDetails = useSelector( state => PULSE.app.redux.selectors.statsplayerprofile.item( state, playerId ) );

  const [ noDataAvailable, setNoDataAvailable] = useState( false );
  const [ chartData, setChartData ] = useState( [] );
  const [ chartType, setChartType ] = useState( 'bar' );
  const [ xLabel, setXLabel ] = useState( getTranslation( 'label.stats.season' ) );
  const [ yLabel, setYLabel ] = useState( getTranslation( 'label.performanceChart.' + STATS_CATEGORY_IDS[ nav.category ] ) );

  useEffect( () => {
    // Fetch career stats
    dispatch( FETCH_PERFORMANCE_CHART_STATS.request( { type: 'career', playerId: playerId } ) );
  }, [] );

  useEffect( () => {
    // Initialise the player profile in the redux store
    dispatch(
        PULSE.app.redux.actions.statsplayerprofile
            .ADD_PLAYER_PROFILE
            .request( { seasonId, playerId } )
    );
  }, [ season ] );

  useEffect( () => {
    // update Stats Category label
    setYLabel( getTranslation( 'label.performanceChart.' + STATS_CATEGORY_IDS[ nav.category ] ) );
  }, [ nav.category ] )

  useEffect(() => {

    setNoDataAvailable( false );
    setXLabel( getTranslation( 'label.stats.season' ) );

    // If Career is chosen we display..
    // Totals - Totals for each season
    // Per Game - N/A
    if ( nav.season.id === -1 && playerData.yearly.length ) {
      // Error handling for when there is no data
      if ( playerData.yearly[ 0 ] === 'no data available' ) {
          setNoDataAvailable( true );
          return;
      }

      if ( dataType.type === 'totals') {
        setChartType('bar');
        setChartData(
          playerData.yearly.map(item => (
            {
              name: item.seasonId?.substr( 4, 4 ),
              uv: item.seasonTotals[ STATS_CATEGORY_IDS[ nav.category ] ],
              amt: item.seasonTotals[ STATS_CATEGORY_IDS[ nav.category ] ],
              benchmark: item.benchmarkedSeasonAverages[ STATS_CATEGORY_IDS[ nav.category ] ].benchmark ?? ''
            }
          ))
        )
      }
    } 
    // If Season is chosen we display..
    // Per Game - Individual totals for each round in the season
    // Totals - Cumulative totals for each round in the season
    else if ( nav.season.id !== -1 ) {

      setXLabel( getTranslation( 'label.match.round' ) );

      // Season stats
      if ( !playerData.season[ season.providerId ] ) {
          // Fetch for rounds data if it doesn't aleady exist
          setChartData( [] );
          dispatch( FETCH_PERFORMANCE_CHART_STATS.request( { type: 'season', playerId: playerId, seasonId: season.providerId } ) );
      } else {
          // Error handling for when there is no data
          if ( playerData.season[ season.providerId ][ 0 ].error ) {
              setNoDataAvailable( true );
              return;
          }
          
          const seasonArray = [...playerData.season[ season.providerId ]];

          if ( dataType.type === 'perGame') {
            setChartType('bar');
            setChartData(
              seasonArray.reverse().map(item => (
                {
                  name: item.roundNumber ?? '',
                  uv: item.benchmarkedStats[ STATS_CATEGORY_IDS[ nav.category ] ].value,
                  amt: item.benchmarkedStats[ STATS_CATEGORY_IDS[ nav.category ] ].value,
                  benchmark: item.benchmarkedStats[ STATS_CATEGORY_IDS[ nav.category ] ].benchmark ?? '',
                  subtext: `${ item.roundName } v ${ item.opponent.teamAbbr }`
                }
              ))
            )
          } else if ( dataType.type === 'totals') {
            const cumulatives = [];
            setChartType('line');
            setChartData(
              seasonArray.reverse().map( ( item, index ) => (
                cumulatives[ index ] = ( index > 0 ) ? item.benchmarkedStats[ STATS_CATEGORY_IDS[ nav.category ] ].value + cumulatives[ index - 1 ] : item.benchmarkedStats[ STATS_CATEGORY_IDS[ nav.category ] ].value,
                {
                  name: item.roundNumber ?? '',
                  uv: cumulatives[ index ],
                  amt: cumulatives[ index ],
                  subtext: `${ item.roundName } v ${ item.opponent.teamAbbr }`
                }
              ) )
            )
          }
      }
    }

  }, [ playerData.yearly, playerData.season, nav.season.id, dataType.type, nav.category ]);

  return (
    <div className="performance-chart">
      {( () => {

         if ( !chartData.length && !noDataAvailable ) {
          // Loader for waiting for data
          return (
            <div style={{ padding: '3.2rem 1.6rem' }}>
              <Loader />
            </div>
          );
        } else if ( noDataAvailable ) {
            // Error handling for when there is no data
            return (
                <div className="performance-chart__empty-state">
                    <EmptyState titleTranslation="label.stats.error.noStatsAvailable" summaryTranslation="label.stats.error.noStatsAvailable.summary"/>
                </div>
            );
        }

        if (chartData.length > 0) {
          return (
            <>
              { chartType === 'bar' && (
                <PerformanceChartBarChart data={ chartData } xLabel={ xLabel } yLabel={ yLabel } playerDetails={ playerDetails } />
              ) }
              { chartType === 'line' && (
                <PerformanceChartLineChart data={ chartData } xLabel={ xLabel } yLabel={ yLabel } playerDetails={ playerDetails } />
              ) }
            </>
          )
        }

      }) () }
    </div>
  )

}
